import { getIncomers, getOutgoers, isEdge, isNode, MiniMap, Position } from 'reactflow';

// Custom nodes
// Custom edges
import Link from '../components/pages/Canvas/ReactFlowUtil/CustomEdges/Link';
import FloatingLink from '../components/pages/Canvas/ReactFlowUtil/CustomEdges/FloatingLink';
import LoopLink from '../components/pages/Canvas/ReactFlowUtil/CustomEdges/LoopLink';
import FormRedirectLink from '../components/pages/Canvas/ReactFlowUtil/CustomEdges/FormRedirectLink';
import SplitConvergeLink from '../components/pages/Canvas/ReactFlowUtil/CustomEdges/SplitConvergeLink';
import GatewayLink from '../components/pages/Canvas/ReactFlowUtil/CustomEdges/GatewayLink';
import { EDGE_TYPES } from './CanvasConstants';

export const edgeTypes = {
  link: Link,
  floatingLink: FloatingLink,
  loopLink: LoopLink,
  formRedirectLink: FormRedirectLink,
  splitConvergeLink: SplitConvergeLink,
  gatewayLink: GatewayLink,
};

export const proOptions = { hideAttribution: true };
export const CANVAS_MIN_ZOOM_OUT = 0.1;
export const DEFAULT_VIEWPORT = {
  x: 0,
  y: 0,
  zoom: 1,
};

export const tooltips = {
  test: '',
  save: '',
  publish: '',
  start: '',
  readme: '',
  access: '',
  files: '',
  logs: '',
  version: '',
  data: '',
  share: '',
  schema: '',
  database: '',
};

// Returns all the nodes and edges together
// Mainly used for our save request to the backend
export const getElements = reactFlowInstance => {
  if (!reactFlowInstance) return [];

  const nodes = reactFlowInstance.getNodes() ? reactFlowInstance.getNodes() : [];
  const edges = reactFlowInstance.getEdges() ? reactFlowInstance.getEdges() : [];

  return [...nodes, ...edges];
};

// Gets a given note in the correct structure
// i.e: removes added props that makes the backend save fail
export const getNoteBackendSaveStructure = note => {
  if (!note) return;
  return {
    id: note.id,
    type: note.type,
    position: note.position,
    data: note.data,
  };
};

// Gets a given link in the correct backend saving structure
// i.e: removes markerEnd as this will cause the graphQL to fail the save
export const getLinkBackendSaveStructure = link => {
  if (!link) return;
  let linkRestructured = {
    ...link,
    arrowHeadType: 'arrowclosed',
  };
  delete linkRestructured.markerEnd; // Remove marker end
  return linkRestructured;
};

// Extracts all nodes from a given array
export const extractNodes = array => {
  if (!array) return [];
  return array.filter(item => item && isNode(item));
};

// Extracts all edges from a given array
export const extractEdges = array => {
  if (!array) return [];
  return array.filter(item => item && isEdge(item));
};

export const isEdgeType = type => {
  if (!type) return false;
  return type in edgeTypes;
};

// Returns true or false depending on if two links
// Are the same
export const isSameLink = (link1, link2) => {
  if (!link1 || !link2) return false;
  if (link1.source !== link2.source) return false;
  if (link1.target !== link2.target) return false;
  if (link1.sourceHandle !== link2.sourceHandle) return false;
  if (link1.targetHandle !== link2.targetHandle) return false;

  return true; // If we reach here, we know that they are the same
};

export const calculateNewItemPostion = (
  currX,
  currY,
  itemWidth,
  itemHeight,
  xOffset,
  yOffset,
  existingCanvasItems
) => {
  let newNoteX = currX + itemWidth + xOffset;
  let newNoteY = currY;

  let itemExists;

  do {
    itemExists = false;

    for (const item of existingCanvasItems) {
      if (item.type !== 'note' && item.type !== 'component') continue;

      const { x, y } = item.position;

      let itemWidth = item.data && item.data.width ? item.data.width : 150;
      let itemHeight = item.data && item.data.height ? item.data.height : 150;

      if (
        newNoteX < x + itemWidth &&
        newNoteX + itemWidth > x &&
        newNoteY < y + itemHeight &&
        newNoteY + itemHeight > y
      ) {
        newNoteY += itemHeight + yOffset; // Move the new item down
        itemExists = true;
        break;
      }
    }
  } while (itemExists);

  return {
    x: newNoteX,
    y: newNoteY,
  };
};

/** LINKS UTIL (HELPER FUNCTIONS FROM REACT FLOW) */

const width = 250; // width of the overall component
const height = 54; // height of the component.

// this helper function returns the intersection point
// of the line between the center of the intersectionNode and the target node
function getNodeIntersection(sourcePosition, targetPosition) {
  // https://math.stackexchange.com/questions/1724792/an-algorithm-for-finding-the-intersection-point-between-a-center-of-vision-and-a
  let w = width / 2;
  const h = height / 2;

  //coordinate of center
  const x2 = sourcePosition.x + w;
  const y2 = sourcePosition.y + h;
  //coordinates of target position
  const x1 = targetPosition.x + w;
  const y1 = targetPosition.y + h;

  w = h; // changing  the radius after finding center to ensure that it just revolve around the component

  const xx1 = (x1 - x2) / (2 * w) - (y1 - y2) / (2 * h);
  const yy1 = (x1 - x2) / (2 * w) + (y1 - y2) / (2 * h);
  const a = 1 / (Math.abs(xx1) + Math.abs(yy1));
  const xx3 = a * xx1;
  const yy3 = a * yy1;
  const x = w * (xx3 + yy3) + x2;
  const y = h * (-xx3 + yy3) + y2;

  return { x, y };
}

// returns the position (top,right,bottom or right) passed node compared to the intersection point
function getEdgePosition(n, intersectionPoint) {
  const nx = Math.round(n.x + height * 2); // this helps identify the start pos of the component in the wrapper
  const ny = Math.round(n.y);
  const px = Math.round(intersectionPoint.x);
  const py = Math.round(intersectionPoint.y);

  if (px <= nx + 1) {
    return Position.Left;
  }
  if (px >= nx + height / 2 - 1) {
    // if it is more than half of the starting position
    return Position.Right;
  }
  if (py <= ny + 1) {
    return Position.Top;
  }
  if (py >= n.y + height / 2 - 1) {
    return Position.Bottom;
  }

  return Position.Top;
}

// returns the parameters (sx, sy, tx, ty, sourcePos, targetPos) you need to create an edge
export function getEdgeParams(sourcePosition, targetPosition) {
  const sourceIntersectionPoint = getNodeIntersection(sourcePosition, targetPosition);
  const targetIntersectionPoint = getNodeIntersection(targetPosition, sourcePosition);

  const sourcePos = getEdgePosition(sourcePosition, sourceIntersectionPoint);
  const targetPos = getEdgePosition(targetPosition, targetIntersectionPoint);

  return {
    sx: sourceIntersectionPoint.x,
    sy: sourceIntersectionPoint.y,
    tx: targetIntersectionPoint.x,
    ty: targetIntersectionPoint.y,
    sourcePos,
    targetPos,
  };
}

/**
 * Checks if adding this connection would create a cycle (backward connection).
 * A backward connection is when the target can reach the source through existing edges.
 * @param {Object} connection - The proposed connection with source and target IDs
 * @param {Array} nodes - All nodes in the canvas
 * @param {Array} edges - All edges in the canvas
 * @returns {boolean} - True if this is a backward connection (creates a cycle)
 */
export function isBackwardConnection(connection, nodes, edges) {
  const target = nodes.find(node => node.id === connection.target);
  if (!target) return false;
  
  // Filter out existing loop links to detect new cycles
  const nonLoopEdges = edges.filter(
    edge => edge.type !== EDGE_TYPES.LOOPLINK && edge.type !== EDGE_TYPES.FORMREDIRECTLINK
  );
  
  const hasCycle = (node, visited = new Set()) => {
    if (node.type !== 'component') return false;
    if (visited.has(node.id)) return false;
    
    visited.add(node.id);
    
    for (const outgoer of getOutgoers(node, nodes, nonLoopEdges)) {
      if (outgoer.id === connection.source) return true;
      if (hasCycle(outgoer, visited)) return true;
    }
    return false;
  };
  
  return hasCycle(target);
}

/**
 * Validates if a connection is allowed.
 * In BPMN mode, cycles (backward connections) are allowed and will be treated as loop edges.
 * @param {Object} connection - The proposed connection
 * @param {Array} nodes - All nodes
 * @param {Array} edges - All edges
 * @param {boolean} useBpmnJoinBehavior - Whether BPMN mode is enabled (allows loops)
 * @returns {boolean} - True if the connection is valid
 */
export function isValidConnection(connection, nodes, edges, useBpmnJoinBehavior = false) {
  const target = nodes.find(node => node.id === connection.target);
  
  // Self-loops are never allowed
  if (target.id === connection.source) return false;
  
  // In BPMN mode, allow backward connections (they become loop edges)
  if (useBpmnJoinBehavior) {
    return true;
  }
  
  // Legacy mode: prevent cycles
  const nonConnectLoopLinks = edges.filter(
    edge => edge.type !== EDGE_TYPES.LOOPLINK && edge.type !== EDGE_TYPES.FORMREDIRECTLINK
  );
  const hasCycle = (node, visited = new Set()) => {
    // we are checking if node type is component as we need to check cycles only in components
    // so this will skip dbNode type o any new component we add later on
    // https://workflow86.atlassian.net/browse/W86-6677
    if (node.type !== 'component') return false;
    if (visited.has(node.id)) return false;

    visited.add(node.id);

    for (const outgoer of getOutgoers(node, nodes, nonConnectLoopLinks)) {
      if (outgoer.id === connection.source) return true;
      if (hasCycle(outgoer, visited)) return true;
    }
    return false;
  };

  return !hasCycle(target);
}

/**
 * Returns true or false depending on if it's an invalid branch path connection or not
 * true -> this means the user tried to connect a branch path to something thats not a conditional_path OR
 *         they tried to connect something thats not a branch path into a conditional path
 * false -> all other cases
 * @param {*} source
 * @param {*} target
 * @param {*} setShowErrorSnackbar
 * @returns
 */
export function isInvalidBranchPathsConnection(source, target, setShowErrorSnackbar) {
  if (!source || !source.data || !target || !target.data) return false;

  /**
   * Branch paths can only be connected to a conditional path (conditional_workflow).
   * Any component can connect into a conditional path.
   */
  if (source.data.type === 'branch_paths' && target.data.type !== 'conditional_workflow') {
    setShowErrorSnackbar(
      <span>
        <b>Branch Path</b> must be connected to <b>Conditional Path</b>
      </span>
    );
    return true;
  }

  return false;
}

/**
 * Returns true or false depending on if it's an invalid loop through list connection or not
 * true -> this means the user tried to nest a loop through list under another loop through list
 * false -> all other cases
 * @param {*} source
 * @param {*} target
 * @param {*} nodes
 * @param {*} edges
 * @param {*} setShowErrorSnackbar
 * @returns
 */
export function isInvalidNestedLoopConnection(source, target, nodes, edges, setShowErrorSnackbar) {
  if (!source || !source.data || !target || !target.data) return false;

  const isLoopThroughList = node => node?.data?.type === 'loop_through_list';

  // If target is loop_through_list, check if any ancestor is also loop_through_list
  if (isLoopThroughList(target)) {
    const hasLoopAncestor = (node, visited = new Set()) => {
      if (visited.has(node.id)) return false;
      visited.add(node.id);

      for (const incomer of getIncomers(node, nodes, edges)) {
        if (isLoopThroughList(incomer)) return true;
        if (hasLoopAncestor(incomer, visited)) return true;
      }
      return false;
    };

    if (isLoopThroughList(source) || hasLoopAncestor(source)) {
      setShowErrorSnackbar(
        'Split into Threads cannot be nested inside another Split into Threads component'
      );
      return true;
    }
  }

  // If source is loop_through_list, check if any descendant is also loop_through_list
  if (isLoopThroughList(source)) {
    const hasLoopDescendant = (node, visited = new Set()) => {
      if (visited.has(node.id)) return false;
      visited.add(node.id);

      for (const outgoer of getOutgoers(node, nodes, edges)) {
        if (isLoopThroughList(outgoer)) return true;
        if (hasLoopDescendant(outgoer, visited)) return true;
      }
      return false;
    };

    if (isLoopThroughList(target) || hasLoopDescendant(target)) {
      setShowErrorSnackbar(
        'Split into Threads cannot be nested inside another Split into Threads component'
      );
      return true;
    }
  }

  return false;
}

/**
 * Returns true or false depending on if it's an invalid converge threads connection
 * true -> converge threads is not downstream of exactly one split into threads
 * false -> all other cases
 * @param {*} source
 * @param {*} target
 * @param {*} nodes
 * @param {*} edges
 * @param {*} setShowErrorSnackbar
 * @returns
 */
export function isInvalidConvergeThreadsConnection(
  source,
  target,
  nodes,
  edges,
  setShowErrorSnackbar
) {
  if (!source || !source.data || !target || !target.data) return false;
  if (target.data.type !== 'converge_threads') return false;

  const isLoopThroughList = node => node?.data?.type === 'loop_through_list';

  const collectLoopAncestors = (node, visited = new Set(), loops = new Set()) => {
    if (!node || visited.has(node.id)) return loops;
    visited.add(node.id);

    for (const incomer of getIncomers(node, nodes, edges)) {
      if (isLoopThroughList(incomer)) loops.add(incomer.id);
      collectLoopAncestors(incomer, visited, loops);
    }
    return loops;
  };

  const sourceLoops = collectLoopAncestors(source);
  if (isLoopThroughList(source)) sourceLoops.add(source.id);

  const targetLoops = collectLoopAncestors(target);

  const combinedLoops = new Set([...sourceLoops, ...targetLoops]);

  if (combinedLoops.size === 0) {
    setShowErrorSnackbar('Merge Threads must be downstream of Split into Threads');
    return true;
  }

  if (combinedLoops.size > 1) {
    setShowErrorSnackbar('Merge Threads can only pair with one Split into Threads');
    return true;
  }

  if (sourceLoops.size === 0) {
    setShowErrorSnackbar('Merge Threads must connect from inside the same Split into Threads');
    return true;
  }

  return false;
}

export const CanvasMinimap = () => {
  return (
    <MiniMap
      pannable
      zoomable
      maskStrokeColor={'var(--color-divider)'}
      maskColor={'var(--color-bg-default)'}
      ariaLabel={'MiniMap'}
      padding={6}
      style={{
        width: '320',
        height: '200',
        borderRadius: '6px',
        overflow: 'hidden',
        backgroundColor: 'var(--color-bg-paper)',
      }}
    />
  );
};
export const validURL = str => {
  let pattern = new RegExp(
    '^(https?:\\/\\/)?' + // protocol
      '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
      '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
      '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*', // port and path
    'i'
  ); // fragment locator
  return !!pattern.test(str);
};

export function areSetsEqual(arr1, arr2) {
  // Handles the case where both are null, undefined, or the same reference
  if (arr1 === arr2) return true;
  // Ensure both are arrays
  if (!Array.isArray(arr1) || !Array.isArray(arr2)) return false;
  // Early return if different lengths
  if (arr1.length !== arr2.length) return false;

  const set1 = new Set(arr1);
  const set2 = new Set(arr2);

  // Check size equality
  if (set1.size !== set2.size) return false;

  for (let item of set1) {
    // Check if every item in set1 exists in set2
    if (!set2.has(item)) return false;
  }

  return true;
}
